/**
 * Contact-level communication opt-out badges + detail.
 *
 * Surfaces, at a glance for support & marketing, which channels a contact has opted out of
 * (or had suppressed) and WHEN. Derives everything from the contact row — no fetch.
 *
 * Channels in the schema: EMAIL opt-out (email_opt_in=false OR email_opt_out_at set) and a
 * separate EMAIL SUPPRESSION (is_email_suppressed, e.g. hard bounce/complaint), plus SMS opt-out
 * (sms_opt_in=false OR sms_opt_out_at set). There is NO separate voice/"do-not-call" column, so
 * no phone-call badge is shown.
 */
import type {
  SmsConsentStatus,
  SmsSingleStatus,
} from "../../services/sms.service";
import { formatDate } from "../../utils/formatters";
import type { Contact } from "../../types/contact.types";

export type OptOutKey =
  | "email_opt_out"
  | "email_suppressed"
  | "sms_opt_out"
  | "do_not_call";

export interface OptOutFlag {
  key: OptOutKey;
  label: string;
  date?: string | null;
  detail?: string | null;
  tone: "red" | "amber";
}

type OptOutFields = Pick<
  Contact,
  | "email_opt_in"
  | "email_opt_out_at"
  | "is_email_suppressed"
  | "email_suppression_updated_at"
  | "email_suppression_reason"
  | "sms_opt_in"
  | "sms_opt_out_at"
  | "do_not_call"
  | "do_not_call_at"
>;

/** Derive the active opt-out / suppression flags for a contact (empty = fully reachable). */
export function getContactOptOuts(
  contact: OptOutFields,
  smsConsentState?: SmsConsentStatus | null,
  /**
   * The single SMS status (migration 998). When given (including null = still unknown) it decides the SMS badge:
   * "SMS opt-out" ONLY for an explicit opt-out, "SMS number blocked" for a block; never for a contact that merely has
   * no recorded opt-in (owner rule 2026-10-26). Omitted (undefined) = the previous behaviour below.
   */
  smsStatus?: SmsSingleStatus | null,
): OptOutFlag[] {
  const flags: OptOutFlag[] = [];

  if (contact.email_opt_in === false || !!contact.email_opt_out_at) {
    flags.push({
      key: "email_opt_out",
      label: "Email opt-out",
      date: contact.email_opt_out_at ?? null,
      tone: "red",
    });
  }
  if (contact.is_email_suppressed === true) {
    flags.push({
      key: "email_suppressed",
      label: "Email suppressed",
      date: contact.email_suppression_updated_at ?? null,
      detail: contact.email_suppression_reason ?? null,
      tone: "amber",
    });
  }
  if (smsStatus !== undefined) {
    if (smsStatus === "opted_out" || smsStatus === "number_blocked") {
      flags.push({
        key: "sms_opt_out",
        label:
          smsStatus === "number_blocked" ? "SMS number blocked" : "SMS opt-out",
        // no date: sms_opt_out_at can be an older imported flag, not the date of the explicit opt-out
        date: null,
        tone: "red",
      });
    }
  } else if (
    smsConsentState === "opted_out" ||
    smsConsentState === "number_suppressed" ||
    (smsConsentState === undefined &&
      (contact.sms_opt_in === false || !!contact.sms_opt_out_at))
  ) {
    // List rows (no status fetched, smsConsentState undefined) only know the contact's flags, which are mostly
    // imported in bulk: say "SMS off", never "SMS opt-out" (owner rule 2026-10-26: opted out only when explicit).
    const listFallback = smsConsentState === undefined;
    flags.push({
      key: "sms_opt_out",
      label:
        smsConsentState === "number_suppressed"
          ? "SMS number blocked"
          : listFallback
            ? "SMS off"
            : "SMS opt-out",
      date: contact.sms_opt_out_at ?? null,
      tone: listFallback ? "amber" : "red",
    });
  }
  if (contact.do_not_call === true) {
    flags.push({
      key: "do_not_call",
      label: "Do not call",
      date: contact.do_not_call_at ?? null,
      tone: "red",
    });
  }
  return flags;
}

const TONE_CLASSES: Record<OptOutFlag["tone"], string> = {
  red: "bg-red-100 text-red-800 ring-red-600/20",
  amber: "bg-amber-100 text-amber-800 ring-amber-600/20",
};

/** Inline badges to render next to the contact name. Renders nothing when reachable. */
export const ContactOptOutBadges: React.FC<{
  contact: OptOutFields;
  className?: string;
  /** null means the effective state is still unknown; do not infer consent from legacy flags. */
  smsConsentState?: SmsConsentStatus | null;
  /** The single SMS status; when passed (null = unknown) it replaces the smsConsentState rule for the SMS badge. */
  smsStatus?: SmsSingleStatus | null;
  /** compact = label only (date moves to the tooltip) — for tight list rows. */
  compact?: boolean;
}> = ({ contact, className, compact = false, smsConsentState, smsStatus }) => {
  const flags = getContactOptOuts(contact, smsConsentState, smsStatus);
  if (flags.length === 0) {
    return null;
  }
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ""}`}>
      {flags.map((f) => (
        <span
          key={f.key}
          title={[
            f.label,
            f.date ? `since ${formatDate(f.date)}` : "date unknown",
            f.detail ? `(${f.detail})` : "",
          ]
            .filter(Boolean)
            .join(" ")}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_CLASSES[f.tone]}`}
        >
          <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clipRule="evenodd"
            />
          </svg>
          {f.label}
          {!compact && f.date ? ` · ${formatDate(f.date)}` : ""}
        </span>
      ))}
    </div>
  );
};

export default ContactOptOutBadges;
